import type {Messages} from "i18n";
import type {PeerActivity} from "core/p2p/peerActivity";

/**
 * Copy selection and rules for the instant/P2P live card.
 *
 * React-free so the rules stay testable and survive any restyle of the card: the component passes the
 * activity snapshot and the `sender.instant` catalog in, and renders whatever comes back. Who is where lives in
 * `./liveRecipientRules`.
 */

/** What the live card's head says about the link (kitecorp/transferify-ui#3383). */
export enum LiveCardState {
	/** The link works and new recipients can reach this sender: the green dot, the live title, the keep-open note. */
	Live = "live",
	/**
	 * The signaling socket is down and web-peer is reconnecting, so nobody new can reach this sender: the yellow dot,
	 * the reconnecting title and the note saying why. The link and the recipient list stay, because recipients
	 * already connected keep downloading over their own peer connections.
	 */
	Reconnecting = "reconnecting",
	/** The api suspended or deleted the transfer: the amber dot and the stopped title, with no note and no link. */
	Stopped = "stopped",
}

/**
 * The card's state, from the activity snapshot. A terminated transfer wins over a reconnect: its link is dead
 * whatever the socket does, and a reconnect would not bring it back.
 */
export function liveCardState({terminated, reconnecting}: PeerActivity): LiveCardState {
	if (terminated !== null) {
		return LiveCardState.Stopped;
	}
	return reconnecting ? LiveCardState.Reconnecting : LiveCardState.Live;
}

/**
 * The card's title (kitecorp/transferify-ui#3320, P1): one sentence while the link works, whoever is or is not
 * downloading, because the recipient list under it says who is where. It replaced a status line that counted
 * downloaders and completions, which the list now shows row by row.
 *
 * A terminated transfer wins (kitecorp/transferify-api-service#418): once the api has suspended or deleted it
 * this sender has stopped serving it, and a title saying the link is live would leave the user watching for
 * downloads that can never happen.
 *
 * While signaling reconnects the title says so in the live title's place (kitecorp/transferify-ui#3383, owner
 * review): a "live" title beside a reconnecting state contradicted itself.
 */
export function liveCardTitle(activity: PeerActivity, instant: Messages["sender"]["instant"]): string {
	switch (liveCardState(activity)) {
		case LiveCardState.Stopped:
			return activity.terminated === "SUSPENDED" ? instant.suspendedStatus : instant.deletedStatus;
		case LiveCardState.Reconnecting:
			return instant.reconnecting;
		case LiveCardState.Live:
			return instant.title;
	}
}

/**
 * The note under the title, or null when there is none: that the link lives only as long as this tab while it
 * works, and that nobody new can connect while signaling reconnects (kitecorp/transferify-ui#3383). A terminated
 * transfer has no note; its notice below the head says what happened.
 */
export function liveCardNote(activity: PeerActivity, instant: Messages["sender"]["instant"]): string | null {
	switch (liveCardState(activity)) {
		case LiveCardState.Stopped:
			return null;
		case LiveCardState.Reconnecting:
			return instant.reconnectingNote;
		case LiveCardState.Live:
			return instant.keepOpen;
	}
}

/**
 * Whether the browser asks before this tab closes or reloads: for as long as the link is live, whoever is or is not
 * downloading (kitecorp/transferify-ui#3724). The file is served from this tab, so closing it kills the link for
 * everyone who has it, not only for a download in flight: a link nobody is downloading from yet, after a recipient's
 * failed attempt for instance, dies just the same. It used to ask only mid-download (kitecorp/transferify-ui#3320, P4).
 * Not after a termination, whose link is already dead.
 */
export function warnsBeforeLeaving(activity: PeerActivity): boolean {
	return showsLiveLink(activity);
}

/**
 * Whether this sender has finished serving a file to at least one recipient (ui#1665).
 *
 * The eligibility half of the signal the card's finished rows show, stated once so the offer and the
 * list cannot disagree about whether the transfer has succeeded. Deliberately NOT gated
 * on the seeding error: a completed download is history, and a file that died afterwards does not
 * un-deliver it.
 */
export function hasServedDownload({servedDownloads}: PeerActivity): boolean {
	return servedDownloads > 0;
}

/**
 * Whether the card should still offer the share link (QR, URL, copy) and its recipient list.
 *
 * False once terminated: the link is dead, so handing the user a copy button for it invites them to
 * send a URL that cannot work. The replacement is a plain statement that it stopped, plus the
 * unchanged "Send another".
 */
export function showsLiveLink({terminated}: PeerActivity): boolean {
	return terminated === null;
}
